import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IProjectMaterial,
  IProjectMaterialInput,
  ISheetDataInput,
  ISitePermit,
  ISubscription,
  IUploadMaterialsExcelInput,
} from '~/client/graph'
import {
  ISaveMaterialMutation,
  RestoreMaterialsDocument,
  UploadMaterialsExcelDocument,
} from '~/client/graph/operations/generated/Materials.generated'
import {
  GetSitePermitAggregationListDocument,
  IGetSitePermitAggregationListQueryVariables,
  ListenToSitePermitDocument,
} from '~/client/graph/operations/generated/SitePermits.generated'
import Material from '~/client/src/shared/models/Material'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import { MaterialStatus } from '../../enums/MaterialStatus'
import { IMaterialViewModel } from '../../models/IMaterialViewModel'
import Workflow from '../../models/Workflow'
import InitialState from '../InitialState'
import GraphExecutorStore from './GraphExecutor.store'
import LocationAttributesStore from './LocationAttributes.store'
import MaterialCategoriesStore from './MaterialCategories.store'
import WorkflowsStore from './Workflows.store'
import WorkflowTypesStore from './WorkflowTypes.store'

const TRANSFER_WORKFLOWS_SUBSCRIPTION_ID = 'transfer-workflows-subscription'

export default class MaterialsStore {
  @observable public isDataReceived = false
  @observable public isRestoring = false
  private readonly materialsMap = observable(new Map<string, Material>())
  private readonly transferPermitsMap = observable(new Map<string, Workflow>())

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly deliveriesStore: DeliveriesStore,
    private readonly materialCategoryStore: MaterialCategoriesStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly permitsStore: WorkflowsStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get list(): Material[] {
    return this.allMaterials.filter(m => !m.isFromConcreteDirect)
  }

  @computed
  public get allMaterials(): Material[] {
    return Array.from(this.materialsMap.values())
  }

  @computed
  private get transferPermits(): Workflow[] {
    return Array.from(this.transferPermitsMap.values())
  }

  @computed
  public get viewModels(): IMaterialViewModel[] {
    return this.list.reduce((list, m) => {
      if (!m.categoryId) {
        return list
      }
      const procurementDataList = m.procurementDataList?.length
        ? m.procurementDataList
        : []

      if (!procurementDataList.length) {
        list.push({
          id: m.id,
          material: m,
          status: m.status,
          relatedDeliveries: m.relatedDeliveries,
          transferForms: this.getTransferForms(m.id),
          description: m.description,
          quantity: m.plannedQuantity,
        })
        return list
      }

      procurementDataList.forEach(data => {
        const installLocation = this.locationAttributesStore.getById(
          data.installLocationId,
        )

        if (!data.procurementId && !installLocation) {
          return
        }

        const relatedDeliveries = m.relatedDeliveries.filter(delivery =>
          delivery.hasMaterialByProcData(
            data.procurementId,
            installLocation?.id,
            procurementDataList,
          ),
        )

        list.push({
          id: data.procurementId
            ? m.id + data.procurementId
            : m.id + installLocation.id,
          material: m,
          status: relatedDeliveries[0]
            ? relatedDeliveries[0].status
            : MaterialStatus.IN_FABRICATION,
          relatedDeliveries,
          transferForms: this.getTransferForms(m.id, data.procurementId),
          procurementId: data.procurementId,
          installLocation,
          deliveryLocation: this.locationAttributesStore.getById(
            data.deliveryLocationId,
          ),
          description: data.description,
          quantity: data.plannedQuantity,
        })
      })
      return list
    }, [] as IMaterialViewModel[])
  }

  @computed
  public get hasProcurementIds(): boolean {
    return this.list.some(m =>
      m.procurementDataList?.some(data => data.procurementId),
    )
  }

  @computed
  public get selectedMaterials(): Material[] {
    const { projectMaterialOptions } = this.state
    if (!projectMaterialOptions) {
      return []
    }

    const { hiddenSubCategoryIds } = projectMaterialOptions
    return this.list.filter(m => !hiddenSubCategoryIds.includes(m.id))
  }

  public async createMaterialIfNotExists(
    materialId: string,
    categoryId: string,
  ): Promise<IProjectMaterial> {
    const existingMaterial =
      categoryId && materialId
        ? this.materialsMap.get(materialId)
        : this.list.find(
            m => (categoryId || '') === (m.categoryId || '') && !m.productName,
          )

    if (existingMaterial) {
      return Promise.resolve(existingMaterial)
    }

    const { id, materialsUploadId } = this.state.activeProject

    return this.save({
      categoryId,
      productName: '',
      projectId: id,
      materialsUploadId: materialsUploadId,
    })
  }

  @action.bound
  public clearList() {
    this.materialsMap.clear()
    this.clearTransferPermits()
  }

  @action.bound
  public clearTransferPermits() {
    this.transferPermitsMap.clear()
  }

  public getByIds = (materialIds: string[] = []): Material[] => {
    return materialIds
      .map(materialId => this.materialsMap.get(materialId))
      .filter(m => !!m)
  }

  @action.bound
  public receiveList(list: IProjectMaterial[]) {
    this.clearList()

    list.forEach(dto => {
      this.materialsMap.set(dto.id, Material.fromDto(dto, this.deliveriesStore))
    })
    this.isDataReceived = true

    // Fetch material transfer permits after receiving materials
    this.fetchTransferWorkflows()
  }

  @action.bound
  public receiveOne(id: string, dto: IProjectMaterial) {
    if (dto) {
      this.materialsMap.set(id, Material.fromDto(dto, this.deliveriesStore))
    } else {
      this.materialsMap.delete(id)
    }
  }

  public async save(
    {
      categoryId,
      productName,
      projectId,
      materialsUploadId,
      description,
      plannedQuantity,
      procurementDataList,
      id,
    }: IProjectMaterial | Material,
    onSuccessCb?: () => void,
    onErrorCb?: () => void,
  ): Promise<IProjectMaterial> {
    const materialInput: IProjectMaterialInput = {
      id: id || null,
      categoryId: categoryId || null,
      projectId,
      materialsUploadId,
      description,
      plannedQuantity,
      productName,
      procurementDataList,
    }
    return new Promise((resolve, reject) => {
      this.eventsStore.dispatch(
        e.SAVE_MATERIAL,
        materialInput,
        (result: ISaveMaterialMutation) => {
          resolve(result?.saveProjectMaterial)
          onSuccessCb?.()
        },
        () => {
          reject()
          onErrorCb?.()
        },
      )
    })
  }

  public hasInstanceById = (materialId: string): boolean => {
    return this.materialsMap.has(materialId)
  }

  public getInstanceById = (materialId: string): Material => {
    if (this.hasInstanceById(materialId)) {
      return this.materialsMap.get(materialId)
    }
  }

  public getProductNameById = (materialId: string): string => {
    return this.getInstanceById(materialId)?.productName
  }

  public async uploadMaterialsExcel(
    fullFilePath: string,
    fileName: string,
    sheetData: ISheetDataInput,
    shouldMergeWithExistingData: boolean,
  ) {
    const { activeProject, user } = this.state

    const data: IUploadMaterialsExcelInput = {
      fullFilePath,
      fileName,
      sheetData,
      shouldMergeWithExistingData,

      projectId: activeProject.id,
      userId: user.id,
    }

    return await this.graphExecutorStore.mutate(UploadMaterialsExcelDocument, {
      data,
    })
  }

  @action.bound
  public async restoreMaterials(materialsUploadId: string): Promise<boolean> {
    this.isRestoring = true

    const mutationResult = await this.graphExecutorStore.mutate(
      RestoreMaterialsDocument,
      {
        projectId: this.state.activeProject.id,
        materialsUploadId,
      },
    )

    this.isRestoring = false

    return mutationResult?.data?.restoreMaterials
  }

  public handlePermitChange(permitDto: ISitePermit) {
    if (!permitDto) return

    const permitType = this.workflowTypesStore.getPermitTypeById(
      permitDto.typeId,
    )
    if (!permitType?.isMaterialTransfer) return

    runInAction(() => {
      const permit = Workflow.fromDto(permitDto)
      if (permit.isDeniedOrFailed) {
        this.transferPermitsMap.delete(permit.id)
      } else {
        this.transferPermitsMap.set(permit.id, permit)
      }
    })
  }

  private get state(): InitialState {
    return this.eventsStore.appState
  }

  private getTransferForms = (
    materialId: string,
    procurementId?: string,
  ): Workflow[] => {
    return this.transferPermits.filter(
      permit => !!permit.getMaterialsByIds(materialId, procurementId).length,
    )
  }

  public getTransferWorkflowById = (id: string): Workflow => {
    if (this.transferPermitsMap.has(id)) {
      return this.transferPermitsMap.get(id)
    }
  }

  public async fetchTransferWorkflows() {
    // Get all material transfer permit type IDs
    const transferTypeIds = this.workflowTypesStore.materialTransferTypes.map(
      type => type.id,
    )

    if (!transferTypeIds.length) return

    await this.fetchPermits(transferTypeIds)
  }

  public async subscribeToTransferWorkflows() {
    this.graphExecutorStore.subscribe(
      ListenToSitePermitDocument,
      {
        projectId: this.state.activeProject.id,
        currentUserId: this.state.user.id,
      },
      true,
      TRANSFER_WORKFLOWS_SUBSCRIPTION_ID,
      (data: ISubscription[]) => {
        data.forEach(({ sitePermit }) => {
          this.handlePermitChange(sitePermit.item)
        })
      },
    )
  }

  public unsubscribeFromWorkflows() {
    this.graphExecutorStore.terminateSubscription(
      TRANSFER_WORKFLOWS_SUBSCRIPTION_ID,
    )
  }

  private async fetchPermits(typeIds: string[]) {
    if (!typeIds?.length) return

    const projectId = this.state.activeProject.id

    const variables: IGetSitePermitAggregationListQueryVariables = {
      filter: {
        typeIds,
        projectId,
      },
      limit: 1_000_000,
    }

    const permitsResult = await this.graphExecutorStore.query(
      GetSitePermitAggregationListDocument,
      variables,
    )

    if (permitsResult?.data?.sitePermitsAggregation?.data) {
      const permits = permitsResult.data.sitePermitsAggregation.data

      // Clear existing permits and add the new ones
      this.clearTransferPermits()

      permits.forEach(permitDto => {
        const permit = Workflow.fromDto(permitDto)
        if (!permit.isDeniedOrFailed) {
          runInAction(() => {
            this.transferPermitsMap.set(permit.id, permit)
          })
        }
      })
    }
  }
}
